import { useState } from "react";
import { NAVY } from "../styles/palette";
import { ACTIVE_THEME } from "../themes";
import { InvoicePreviewModal } from "./InvoicePreviewModal";
import { InvoiceEditModal } from "./InvoiceEditModal";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";

export function ClientBillingTab({ invoices, client, onApprove, onRetryCharge, onIssueRefund, onVoidInvoice, onEdit, onRefreshInvoices, showToast }) {
  const [previewInv, setPreviewInv] = useState(null);
  const [editInv,    setEditInv]    = useState(null);
  const [hoveredId,  setHoveredId]  = useState(null);
  const isMobile = useIsMobile();
  const apple = ACTIVE_THEME === "apple-native";
  // The preview / edit sheets are owned here (Invoices.js registers its own), so the
  // iOS back-swipe closes them instead of leaving the profile.
  useBackClose(!!previewInv, () => setPreviewInv(null));
  useBackClose(!!editInv,    () => setEditInv(null));

  function statusBadge(status) {
    const map = { paid:"green", open:"gold", draft:"purple", uncollectible:"red", void:"gray", refunded:"gray", partially_refunded:"gold" };
    const label = { paid:"Paid", open:"Open", draft:"Draft", uncollectible:"Uncollectible", void:"Void", refunded:"Refunded", partially_refunded:"Partially refunded" };
    return <span className={`badge badge-${map[status]||"gray"}`} style={{textTransform:"capitalize",fontSize:10}}>
      {label[status]||status}
    </span>;
  }
  // A partially-refunded invoice is still a paid invoice: don't edit/void it, but DO
  // allow another (partial) refund — issue-refund supports it, the button just hid.
  const isPaidLike = (s) => s==="paid" || s==="partially_refunded";

  // The action buttons for an invoice — identical set in both layouts.
  const actions = (inv) => (<>
    <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setPreviewInv(inv)}>📄 Preview</button>
    {!isPaidLike(inv.status)&&inv.status!=="void"&&<button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setEditInv(inv)}>✏️ Edit</button>}
    {!isPaidLike(inv.status)&&inv.status!=="void"&&inv.status!=="refunded"&&<button className="btn btn-sm" style={{background:"#f4f5f7",color:"#42526e",border:"none",fontSize:11}} onClick={()=>onVoidInvoice&&onVoidInvoice(inv)}>⊘ Void</button>}
    {inv.status==="draft"&&(
      inv.stripe_invoice_id
        ? <button className="btn btn-gold btn-sm" style={{fontSize:11}} onClick={()=>onApprove&&onApprove(inv)}>⚡ Finalize &amp; Charge</button>
        : <button className="btn btn-gold btn-sm" style={{fontSize:11}} onClick={()=>onApprove&&onApprove(inv)}>✓ Approve &amp; Charge</button>
    )}
    {inv.status==="open"&&inv.stripe_invoice_id&&(
      <button className="btn btn-gold btn-sm" style={{fontSize:11}} onClick={()=>onApprove&&onApprove(inv)}>⚡ Charge Now</button>
    )}
    {inv.stripe_hosted_url&&inv.status!=="paid"&&(
      <a href={inv.stripe_hosted_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{fontSize:11}}>👁 View on Stripe</a>
    )}
    {inv.status==="uncollectible"&&<button className="btn btn-sm" style={{background:"#ffebe6",color:"#bf2600",border:"none",fontSize:11}} onClick={()=>onRetryCharge&&onRetryCharge(inv)}>🔄 Retry</button>}
    {isPaidLike(inv.status)&&<button className="btn btn-sm" style={{background:"#ffebe6",color:"#bf2600",border:"none",fontSize:11}} onClick={()=>onIssueRefund&&onIssueRefund(inv)}>↩ Refund</button>}
  </>);

  const modals = (<>
    {previewInv&&<InvoicePreviewModal invoice={previewInv} onClose={()=>setPreviewInv(null)}/>}
    {editInv&&<InvoiceEditModal invoice={editInv} onSave={(updated)=>{ onEdit&&onEdit(updated); setEditInv(null); }} onSaveAndPreview={(updated)=>{ onEdit&&onEdit(updated); setEditInv(null); setPreviewInv(updated); }} onClose={()=>setEditInv(null)} onApprove={(inv)=>{ onApprove&&onApprove(inv); setEditInv(null); }}/>}
  </>);

  // ── apple-native: a live, hover-reactive invoice list (clean at rest, actions reveal on hover) ──
  if (apple) {
    return (
      <div>
        {invoices.length===0 && <div className="empty"><p>No invoices for this client</p></div>}
        {invoices.map(inv=>{
          // Touch has no hover: on phones the actions (the ONLY place Charge / Void /
          // Refund live for a client) are always expanded and never height-clipped.
          const hov  = !isMobile && hoveredId===inv.id;
          const show = isMobile || hov;
          return (
            <div key={inv.id}
              onMouseEnter={()=>setHoveredId(inv.id)} onMouseLeave={()=>setHoveredId(null)}
              onClick={(e)=>{ if(e.target.closest("button")||e.target.closest("a")) return; setPreviewInv(inv); }}
              style={{
                padding: isMobile ? "14px 4px" : "15px 16px", borderRadius:14, cursor:"pointer", marginBottom:2,
                background: hov ? "var(--accent-weak)" : "transparent", transition:"background .15s ease",
                borderBottom: hov ? "1px solid transparent" : "1px solid var(--border)",
              }}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontWeight:600,fontSize:13.5,color:"var(--text)"}}>{inv.invoice_number||inv.date}</span>
                    {statusBadge(inv.status)}
                  </div>
                  <div style={{fontSize:12,color:"var(--text2)",marginTop:3}}>{inv.date}{inv.period?` · Period ${inv.period}`:""}{inv.notes?` · ${inv.notes}`:""}</div>
                </div>
                <div style={{fontWeight:600,fontSize:18,letterSpacing:"-.02em",color:Number(inv.amount)<0?"var(--danger)":"var(--text)",flexShrink:0}}>${Math.abs(Number(inv.amount)).toFixed(2)}</div>
              </div>
              <div style={{
                display:"flex",gap:6,flexWrap:"wrap",overflow: isMobile ? "visible" : "hidden",
                maxHeight: isMobile ? "none" : (hov ? 60 : 0), opacity: show ? 1 : 0, marginTop: show ? 12 : 0,
                transition:"max-height .22s ease, opacity .2s ease, margin .22s ease",
              }}>
                {actions(inv)}
              </div>
            </div>
          );
        })}
        {modals}
      </div>
    );
  }

  // ── default: original card layout (unchanged) ──
  return (
    <div>
      {invoices.length===0&&<div className="empty"><p>No invoices for this client</p></div>}
      {invoices.map(inv=>(
        <div key={inv.id} className="card" style={{marginBottom:10,cursor:"pointer",transition:"box-shadow .15s"}}
          onClick={(e)=>{ if(e.target.tagName==="BUTTON"||e.target.closest("button")) return; setPreviewInv(inv); }}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{fontFamily:"Syne",fontWeight:700,fontSize:12,color:NAVY}}>{inv.invoice_number}</span>
                {statusBadge(inv.status)}
              </div>
              <div style={{display:"flex",gap:12,fontSize:12,color:"#42526e"}}>
                <span>📅 {inv.date}</span>
                {inv.period&&<span>Period: {inv.period}</span>}
              </div>
              {inv.notes&&<div style={{fontSize:11,color:"#8993a4",marginTop:4,lineHeight:1.4}}>{inv.notes}</div>}
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontFamily:"Syne",fontWeight:800,fontSize:18,color:inv.amount<0?"#bf2600":NAVY}}>${Math.abs(Number(inv.amount)).toFixed(2)}</div>
              <div style={{fontSize:10,color:"#8993a4"}}>{inv.frequency}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
            {actions(inv)}
          </div>
        </div>
      ))}
      {modals}
    </div>
  );
}
